// v2 flower — every colour variant gets its OWN blossom shape on a leafy stem: a
// pink daisy of flat radiating petals, a yellow tulip cup, a nodding lavender
// bellflower, a blue star of pointed petals and an orange poppy bowl — so a
// meadow reads as five different species rather than one flower in five paints.
// Ported 1:1 from age-of-mcp's v2/flower.ts (the source uses no shared helpers —
// every primitive is inline).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const TAU = Math.PI * 2;
// Per-variant stem heights — each species carries its blossom a little
// differently, all landing near the classic 0.09-tile flower height.
const STEM = [0.07, 0.06, 0.066, 0.072, 0.064];

function flowerParts(v: number): Part[] {
  const variant = v % 5;
  const stemH = STEM[variant]!;
  const parts: Part[] = [
    // stem and two small leaves, shared by every species
    { geometry: new THREE.CylinderGeometry(0.0045, 0.006, stemH, 4),
      color: "#3a7a30", position: [0, stemH / 2, 0] },
    { geometry: new THREE.BoxGeometry(0.026, 0.004, 0.011),
      color: "#4c8a3e", position: [0.014, stemH * 0.42, 0.003],
      rotation: [0, 0.5, 0.45] },
    { geometry: new THREE.BoxGeometry(0.022, 0.004, 0.01),
      color: "#447e38", position: [-0.012, stemH * 0.3, -0.004],
      rotation: [0, 2.4, -0.4] },
  ];
  const top = stemH;
  if (variant === 0) {
    // pink daisy — flat radiating petals around a golden heart
    parts.push({ geometry: new THREE.CylinderGeometry(0.011, 0.009, 0.008, 6),
      color: "#f5c84b", position: [0, top + 0.006, 0] });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU;
      parts.push({
        geometry: new THREE.BoxGeometry(0.024, 0.004, 0.012),
        color: "#f06e7e",
        position: [Math.cos(a) * 0.019, top + 0.004, Math.sin(a) * 0.019],
        rotation: [0, -a, 0.16],
      });
    }
  } else if (variant === 1) {
    // yellow tulip — a closed cup with an amber tip
    parts.push({ geometry: new THREE.CylinderGeometry(0.015, 0.006, 0.03, 6),
      color: "#f4d35e", position: [0, top + 0.014, 0] });
    parts.push({ geometry: new THREE.ConeGeometry(0.013, 0.014, 6),
      color: "#e8b83c", position: [0, top + 0.034, 0] });
  } else if (variant === 2) {
    // lavender bellflower — a bell nodding off the stem tip
    parts.push({ geometry: new THREE.CylinderGeometry(0.003, 0.004, 0.02, 4),
      color: "#3a7a30", position: [0.008, top + 0.007, 0], rotation: [0, 0, -0.7] });
    parts.push({ geometry: new THREE.ConeGeometry(0.015, 0.026, 6),
      color: "#dcbcff", position: [0.018, top + 0.002, 0] });
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.005, 0),
      color: "#f5efff", position: [0.018, top - 0.012, 0] });
  } else if (variant === 3) {
    // blue star — five pointed petals around a pale heart
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.008, 0),
      color: "#fdf6e0", position: [0, top + 0.005, 0] });
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU;
      parts.push({
        geometry: new THREE.TetrahedronGeometry(0.013, 0),
        color: "#8ad8ff",
        position: [Math.cos(a) * 0.016, top + 0.004, Math.sin(a) * 0.016],
        rotation: [0.3, -a, 0.3],
      });
    }
  } else {
    // orange poppy — an open bowl with a dark heart
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.018, 0),
      color: "#ffb454", position: [0, top + 0.008, 0], scale: [1, 0.5, 1] });
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.007, 0),
      color: "#4a3326", position: [0, top + 0.016, 0] });
  }
  return parts;
}

export const flowerV2 = defineModel({
  id: "flower-v2",
  batchId: "fable",
  name: "Flower (v2)",
  tags: ["scenery","flower","low-poly","age-of-mcp","fable"],
  summary: "Low-poly flower.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => flowerParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => flowerParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => flowerParts(2) },
    { id: "variant-3", label: "Variant 4", parts: () => flowerParts(3) },
    { id: "variant-4", label: "Variant 5", parts: () => flowerParts(4) },
  ],
  animations: [],
});
